import {
    ModifierGroupContract,
    POSVariantGroup,
    OrderChannelContract,
    DiscountContract,
    CountryContract,
    NotificationChannelContract,
    PosTerminalMenuSlimContract,
    BrandStatusContract,
    VariantOptionModifierContract,
    LocationOrderChannelContract,
    UnavailableItemsContract,
    CustomerDisplaySettingContract,
} from 'models'
import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from 'react'
import {
    MenuViewModel,
    EntityModifierViewModel,
    ModifierOptionLookup,
    ComboViewModel,
    IOnlineStoreWithBrand,
    PriceListData,
    ItemViewModel,
    CategoryViewModel,
    EntityModifiersMap,
    ItemMap,
    OrderItemViewModel,
} from 'store/models/sellModels'
import SplashPage from 'pages/SplashPage'
import posEvent, {
    POSEventArg,
    POSEventType,
} from 'websockets/events/posHubEvent'
import { ItemAvailabilityChangeContract } from 'interfaces'
import { useDispatch } from 'react-redux'
import { useAppSelector } from 'store/useStoreHook'
import ErrorBoundaryPage from 'pages/ErrorBoundaryPage'
import useAuth from 'hooks/useAuth/useAuth'
import { getDefaultReceiptTemplate } from 'store/actions/receiptTemplate.action'
import notificationChannelService from 'Services/api/notificationChannel.service'
import orderChannelService from 'Services/api/orderChannel.service'
import posTerminalService from 'Services/api/posTerminal.service'
import productService from 'Services/api/product.service'
import productCategoryService from 'Services/api/productCategory.service'
import variantService from 'Services/api/variant.service'
import { isOpenItemId, OPEN_ITEM_ID } from 'constant/magicNumber'
import { isEmpty, keyBy, values } from 'lodash'
import {
    createPriceListData,
    createCategoryDict,
    createProductDict,
    createMenuDict,
    createMenus,
    bindingEntityToModifierGroup,
    prepareAndAddOptionToModifierGroupsMap,
    prepareAndAddOptionToComboChoicesMap,
    comboChoiceSorter,
    bindingBrandToMenu,
    createOnlineStoreMenu,
    updateUnavailableItems,
    applyMenuSectionToItem,
} from 'store/utils/normalizeDependencyData'
import { isTimeEventDiscountAvailable } from '../discount/discountMapper/discountFilter/discountFilter'
import { IDefaultReceiptTemplate } from 'store/reducers/receiptTemplate.reducer'
import brandService from 'Services/brand.service'
import toast from 'utilities/toast/toast'
import useFetchApi from 'hooks/useFetchApi/useFetchApi'
import customerDisplaySettingService from 'Services/api/customerDisplaySetting.service'
import { validateAvailability } from 'utilities/availability/availabilityHelper'
import { usePosContext } from './PosProvider'
import {
    isSameOrderItem,
    isUseSamePriceList,
} from 'components/Sell/utils/common'

export interface ISaleDependencies {
    menus: MenuViewModel[]
    terminalMenu: PosTerminalMenuSlimContract
    modifierList: EntityModifierViewModel[]
    modifierGroupsMap: Record<string, ModifierGroupContract>
    modifierOptionsMap: ModifierOptionLookup
    variantGroupsMap: Record<string, POSVariantGroup>
    /** Map of <Item.itemID of combo item, comboViewModel (`ComboChoice` minified model)> */
    comboChoicesMap: Record<string, ComboViewModel[]>
    orderChannelsMap: Record<number, OrderChannelContract>
    discounts: DiscountContract[]
    country: CountryContract
    onlineStores: IOnlineStoreWithBrand[]
    selectOnlineStoreId?: string
    notificationChannels: NotificationChannelContract[]
    unavailableProducts: UnavailableItemsContract
    unavailableVariantOptionIds: string[]
    priceListData: PriceListData
    brands: BrandStatusContract[]
    itemsMap: ItemMap
    openItem: ItemViewModel | null | undefined
    defaultReceiptMarkups: IDefaultReceiptTemplate
    variantOptionModifierMap: Map<string, VariantOptionModifierContract[]>

    /** available online order channels in current location */
    onlineOrderChannels: LocationOrderChannelContract[]

    entityModifiersMap: EntityModifiersMap
}

interface AddItemCountingResult {
    newOrderItems: OrderItemViewModel[]
    newAddedItem: OrderItemViewModel
}

export interface ISaleDependenciesContext extends ISaleDependencies {
    isReady: boolean
    isLoading: boolean
    cfdSetting: CustomerDisplaySettingContract | undefined

    /**
     * Add `newItem` to `orderItems` with regarding to merge the same item
     * If `newItem` is the same item in `orderItems`, this function will increase the matched item quantity (`OrderItemViewModel.amount`)
     * @param preventEditLines list of Order Line ID which cannot be edited. these lines will be excluded from merging
     * @returns the object contains new `orderItems` list and ref to `newAddedItem`. If `newItem` is the same item, the references is the matched item in `orderItems`
     */
    addItemCounting: (
        orderItems: OrderItemViewModel[],
        newItem: OrderItemViewModel,
        preventEditLines?: string[],
    ) => AddItemCountingResult
}

const SaleDependenciesContext = createContext<ISaleDependenciesContext>(
    {} as ISaleDependenciesContext,
)
type TDeps = Omit<
    ISaleDependencies,
    | 'country'
    | 'defaultReceiptMarkups'
    | 'unavailableProducts'
    | 'unavailableVariantOptionIds'
>
const initialState: TDeps = {
    menus: [],
    itemsMap: {},
    modifierList: [],
    modifierGroupsMap: {},
    modifierOptionsMap: {},
    variantGroupsMap: {},
    variantOptionModifierMap: new Map<
        string,
        VariantOptionModifierContract[]
    >(),

    comboChoicesMap: {},
    orderChannelsMap: {},
    discounts: [],
    onlineStores: [],
    notificationChannels: [],
    priceListData: {
        basePriceList: {
            isBasePriceList: true,
            priceListItems: [],
            priceListModifierOptions: [],
        },
        modifierGroupPriceListMap: {},
        menuPriceListMap: {},
    },
    openItem: null,
    terminalMenu: {},
    brands: [],
    onlineOrderChannels: [],
    entityModifiersMap: {},
}
const useGetSaleProductDependencies = () => {
    const dispatch = useDispatch()
    const [isLoading, setIsLoading] = useState(false)
    const [isReady, setIsReady] = useState(false)
    const [isFailedToLoad, setFailed] = useState(false)
    const defaultReceiptTemplate = useAppSelector(
        (state) => state.receiptTemplate.defaultReceiptTemplate,
    )
    const country = useAppSelector((state) => state.operation.currentCountry)
    const [dedps, setDeps] = useState<TDeps>(initialState)
    const [unavailableProducts, setUnavailableProducts] = useState<
        UnavailableItemsContract
    >({
        unavailableSalesItems: [],
        unavailableStockItems: [],
    })
    const [
        unavailableVariantOptionIds,
        setUnavailableVariantOptionIds,
    ] = useState<string[]>([])
    const onUpdateUnavailableItem = useCallback(
        (itemChanges: ItemAvailabilityChangeContract[]) => {
            const {
                unavailableStockItems,
                unavailableSalesItems,
            } = updateUnavailableItems(unavailableProducts)(itemChanges)

            const items = values(dedps.itemsMap)
            items.forEach((i) => {
                i.isAvailableForSelection = unavailableSalesItems.every(
                    (ui) => ui.itemID !== i.itemId,
                )
            })
            setUnavailableProducts({
                unavailableStockItems,
                unavailableSalesItems,
            })
        },
        [dedps.itemsMap, unavailableProducts],
    )

    const loadDependencies = useCallback(async () => {
        try {
            setIsLoading(true)
            setIsReady(false)
            setFailed(false)

            const [
                categories,
                posData,
                variantGroups,
                orderChannels,
                terminalMenu,
                notificationChannels,
                unavailableProducts,
                locationOrderChannels,
                brands,
            ] = await Promise.all([
                productCategoryService.getProductCategoryLookupList(),
                productService.getPOSProducts(),
                variantService.getVariantForPOS(),
                orderChannelService.getOrderChannels(),
                posTerminalService.getCurrentTerminalMenu(),
                notificationChannelService.getNotificationChannels(),
                productService.getUnavailableProducts(),
                orderChannelService.getLocationOrderChannels(),
                brandService.getLocationBrandsStatus(),
            ])

            const rawModifierGroups = posData.data.modifierGroups ?? []
            const rawMenus = posData.data.menus ?? []
            const orderChannelsMap = keyBy(
                orderChannels.data,
                (o) => o.orderChannelID ?? '',
            )
            const variantGroupsMap = keyBy(
                variantGroups.data,
                (v) => v.variantGroupID ?? '',
            )
            const modifierGroupsMap = keyBy(
                rawModifierGroups,
                (m) => m.modifierGroupID ?? '',
            )
            const modifierOptionsMap = keyBy(
                posData.data.modifierOptions,
                (m) => m.modifierOptionID ?? '',
            )
            let variantOptionModifierMap = new Map<
                string,
                VariantOptionModifierContract[]
            >()
            posData.data.variantOptionModifiers?.forEach((vOptMod) => {
                if (!variantOptionModifierMap.has(vOptMod.variantOptionID!!))
                    variantOptionModifierMap.set(
                        vOptMod.variantOptionID!!,
                        posData.data.variantOptionModifiers?.filter(
                            (i) =>
                                i.variantOptionID === vOptMod.variantOptionID,
                        ) ?? [],
                    )
            })
            const priceListData = createPriceListData(
                posData.data.priceLists ?? [],
                rawMenus,
            )
            const comboChoicesMap: { [key: string]: ComboViewModel[] } = {}
            const dCategories: {
                [key: string]: CategoryViewModel
            } = createCategoryDict(categories.data)
            // has product and combo excluded variant product
            const dItem = createProductDict(posData.data.products)
            const openItem = dItem[OPEN_ITEM_ID]

            const menuItems = rawMenus.flatMap((m) => m.menuItems ?? [])

            // Generate dMenu to store all menu that belong to current POS Terminal
            const dMenu = createMenuDict(rawMenus, terminalMenu.data)

            // System Menu contain all item in this pos terminal
            const systemMenu = Object.values(dMenu).find(
                (m) => !m.isUserDefinedMenu,
            )

            // Add Only item belong to current terminal to system menu (All)
            if (
                systemMenu &&
                (isEmpty(terminalMenu.data.menuIds ?? []) ||
                    terminalMenu.data.menuIds?.includes(systemMenu.menuId))
            ) {
                values(dItem)
                    .filter((i) => !isOpenItemId(i.itemId))
                    .forEach((i) => {
                        menuItems.push({
                            menuID: systemMenu!.menuId,
                            itemID: i.itemId,
                        })
                    })
            }

            applyMenuSectionToItem(rawMenus, dItem, dMenu)
            const menus = createMenus(menuItems, dMenu, dItem, dCategories)
            const entityModifiersMap = bindingEntityToModifierGroup(
                modifierGroupsMap,
                posData.data,
            )

            const modifierList = values(entityModifiersMap)

            posData.data.modifierOptions?.forEach((opt) => {
                prepareAndAddOptionToModifierGroupsMap(
                    opt,
                    modifierGroupsMap,
                    dItem,
                )
            })

            posData.data.comboChoices?.forEach((comboChoice) => {
                prepareAndAddOptionToComboChoicesMap(
                    comboChoicesMap,
                    comboChoice,
                    dItem,
                    variantGroupsMap,
                    priceListData,
                )
            })

            Object.keys(comboChoicesMap).forEach((comboID) => {
                if (!dItem[comboID]) return
                // Sort choices in combo
                comboChoicesMap[comboID].sort(comboChoiceSorter)
                //disable combo if some choice unable to select at least 1 item

                dItem[comboID].isAvailableForSelection =
                    dItem[comboID].isAvailableForSelection &&
                    !comboChoicesMap[comboID].some((choice) =>
                        choice.comboItems.every((ci) => {
                            return !dItem[ci.itemID].isAvailableForSelection
                        }),
                    )

                comboChoicesMap[comboID].sort((a, b) => {
                    if (a.sortNumber && b.sortNumber) {
                        return a.sortNumber - b.sortNumber
                    }
                    return a.createdOn.localeCompare(b.createdOn)
                })
            })
            const menusWithBrand = bindingBrandToMenu(menus, dItem)
            const onlineStoresWithBrand = createOnlineStoreMenu(
                posData.data,
                menusWithBrand,
            )

            setUnavailableVariantOptionIds(
                variantGroups.data.flatMap(
                    (g) =>
                        g.variantOptions
                            ?.filter((a) => !a.isAvailable)
                            .map((a) => a.variantOptionID!) ?? [],
                ),
            )
            setUnavailableProducts(unavailableProducts.data)

            setDeps({
                menus: menusWithBrand,
                openItem,
                itemsMap: dItem,
                modifierList,
                modifierGroupsMap,
                modifierOptionsMap,
                variantGroupsMap,
                variantOptionModifierMap,
                comboChoicesMap,
                orderChannelsMap,
                discounts:
                    posData.data.discounts?.filter(
                        (f) => f.isActive && isTimeEventDiscountAvailable(f),
                    ) ?? [],
                onlineStores: onlineStoresWithBrand ?? [],
                notificationChannels: notificationChannels.data,
                priceListData,
                onlineOrderChannels: locationOrderChannels.data.filter(
                    (c) => c.isOnlineChannel,
                ),
                terminalMenu: terminalMenu.data,
                brands: brands.data.filter((a) => a.isActive),
                entityModifiersMap,
            })
            setIsReady(true)
        } catch (err) {
            setFailed(true)
            setIsReady(false)
            toast.error('Unable to load sale data.')
            console.error(err)
        } finally {
            setIsLoading(false)
        }
    }, [])

    const loadBaseDependencies = useCallback(() => {
        loadDependencies()
        dispatch(getDefaultReceiptTemplate())
    }, [dispatch, loadDependencies])
    const data = useMemo(() => {
        return {
            ...dedps,
            country: country || {},
            defaultReceiptMarkups: defaultReceiptTemplate ?? {},
            unavailableProducts,
            unavailableVariantOptionIds,
        }
    }, [
        dedps,
        country,
        defaultReceiptTemplate,
        unavailableProducts,
        unavailableVariantOptionIds,
    ])
    return {
        loadBaseDependencies,
        isReady,
        isLoading,
        isFailedToLoad,
        data,
        updateUnavailableItems: onUpdateUnavailableItem,
    }
}

function ErrorContainer() {
    // reload after 5 seconds
    useEffect(() => {
        window.setTimeout(() => {
            window.location.replace('/')
        }, 5000)
    }, [])

    return <ErrorBoundaryPage />
}

export function SaleDependenciesContextProvider({
    children,
}: {
    children: React.ReactNode
}) {
    const {
        loadBaseDependencies,
        data,
        isReady,
        isFailedToLoad,
        updateUnavailableItems,
        isLoading,
    } = useGetSaleProductDependencies()
    const { customerFacingDisplay } = useAppSelector((s) => s.operation.data)
    const client = useAppSelector((state) => state.client.currentClient)
    const [cfdSetting, getCustomerDisplaySetting] = useFetchApi(
        customerDisplaySettingService.getCustomerDisplaySettingByClientId,
    )

    const { showTimeAvailabilityOverrideFilter } = usePosContext()

    // Display available menus only
    const displayMenus = useMemo(() => {
        return (
            data.menus.filter(
                (i) =>
                    showTimeAvailabilityOverrideFilter ||
                    validateAvailability(i.availabilities),
            ) ?? []
        )
    }, [data.menus, showTimeAvailabilityOverrideFilter])

    const { isAuthenticated } = useAuth()

    // shared common helper functions that needs POS dependencies data to process
    // instead create these functions as pure functions, provide in this ways can save the number of parameters passed to the functions
    const addItemCounting = useCallback(
        (
            orderItems: OrderItemViewModel[],
            newItem: OrderItemViewModel,
            preventEditLines: string[] = [],
        ): AddItemCountingResult => {
            const exProd = orderItems.find(
                (i) =>
                    !preventEditLines.includes(i.id) &&
                    isSameOrderItem(i, newItem) &&
                    isUseSamePriceList(
                        data.menus,
                        data.priceListData,
                        i,
                        newItem,
                    ),
            )
            if (exProd) {
                exProd.amount += newItem.amount
                exProd.isSaved = false
                return {
                    newOrderItems: orderItems,
                    newAddedItem: exProd,
                }
            } else {
                return {
                    newOrderItems: [...orderItems, newItem],
                    newAddedItem: newItem,
                }
            }
        },
        [data.menus, data.priceListData],
    )

    useEffect(() => {
        if (customerFacingDisplay && client?.clientID)
            getCustomerDisplaySetting(client.clientID)
    }, [client?.clientID, customerFacingDisplay, getCustomerDisplaySetting])
    useEffect(() => {
        if (isAuthenticated) {
            loadBaseDependencies()
        }
    }, [loadBaseDependencies, isAuthenticated, getCustomerDisplaySetting])

    useEffect(() => {
        const subscription = posEvent.subscribe((e: POSEventArg) => {
            if (e.type === POSEventType.OnItemAvailabilityChanged) {
                updateUnavailableItems(e.params)
            }
        })

        return () => {
            subscription.unsubscribe()
        }
    }, [updateUnavailableItems])

    return (
        <SaleDependenciesContext.Provider
            value={{
                ...data,
                addItemCounting,
                menus: displayMenus,
                isReady,
                isLoading,
                cfdSetting,
            }}
        >
            {isLoading ? (
                <SplashPage />
            ) : isReady ? (
                children
            ) : isFailedToLoad ? (
                <ErrorContainer />
            ) : (
                <SplashPage />
            )}
        </SaleDependenciesContext.Provider>
    )
}

export const useSaleDependenciesContext = () => {
    return useContext(SaleDependenciesContext)
}
